/**
 * Hook for client-side filtering, sorting, and searching the company list.
 * All operations are synchronous O(n) with useMemo — fast for < 200 companies.
 *
 * Covers: AC-001 (search), AC-002 (sort), AC-003 (filter), AC-004 (empty state),
 *         AC-005 (session state), AC-006 (clear on Escape)
 */

import { useState, useMemo, useCallback } from 'react';
import type { CompanyListItem, CompanyType } from '../types';

export type CompanySortField = 'name' | 'kb_completeness' | 'updated_at' | 'company_type';
export type SortDirection = 'asc' | 'desc';
export type CompanyTypeFilter = 'all' | 'public' | 'private';
export type KBStatusFilter = 'all' | 'complete' | 'building' | 'stale';

export interface UseCompanyListFiltersReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortBy: CompanySortField;
  setSortBy: (field: CompanySortField) => void;
  sortOrder: SortDirection;
  setSortOrder: (order: SortDirection) => void;
  filterType: CompanyTypeFilter;
  setFilterType: (type: CompanyTypeFilter) => void;
  filterStatus: KBStatusFilter;
  setFilterStatus: (status: KBStatusFilter) => void;
  filteredCompanies: CompanyListItem[];
  resultCount: number;
  totalCount: number;
  hasActiveFilters: boolean;
  clearFilters: () => void;
}

/**
 * Determines whether a company type matches the "private" filter.
 * Groups pe_backed and subsidiary under "private" alongside literal private.
 */
function isPrivateType(companyType: CompanyType): boolean {
  return companyType === 'private' || companyType === 'pe_backed' || companyType === 'subsidiary';
}

export function useCompanyListFilters(companies: CompanyListItem[]): UseCompanyListFiltersReturn {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<CompanySortField>('name');
  const [sortOrder, setSortOrder] = useState<SortDirection>('asc');
  const [filterType, setFilterType] = useState<CompanyTypeFilter>('all');
  const [filterStatus, setFilterStatus] = useState<KBStatusFilter>('all');

  const hasActiveFilters = useMemo(
    () =>
      searchQuery !== '' ||
      filterType !== 'all' ||
      filterStatus !== 'all' ||
      sortBy !== 'name' ||
      sortOrder !== 'asc',
    [searchQuery, filterType, filterStatus, sortBy, sortOrder]
  );

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSortBy('name');
    setSortOrder('asc');
    setFilterType('all');
    setFilterStatus('all');
  }, []);

  // PERF: O(n) filtering + O(n log n) sorting — acceptable for < 200 companies
  const filteredCompanies = useMemo(() => {
    let result = [...companies];

    // Search filter: case-insensitive substring match against name, domain, ticker
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((company) => {
        const name = company.name.toLowerCase();
        const domain = company.domain.toLowerCase();
        const ticker = (company.ticker || '').toLowerCase();
        return name.includes(query) || domain.includes(query) || ticker.includes(query);
      });
    }

    // Type filter
    if (filterType === 'public') {
      result = result.filter((c) => c.company_type === 'public');
    } else if (filterType === 'private') {
      result = result.filter((c) => isPrivateType(c.company_type));
    }

    // KB status filter
    if (filterStatus === 'complete') {
      result = result.filter((c) => (c.kb_completeness ?? 0) >= 0.7);
    } else if (filterStatus === 'building') {
      result = result.filter((c) => c.kb_is_building);
    } else if (filterStatus === 'stale') {
      result = result.filter((c) => (c.kb_completeness ?? 0) < 0.4);
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'kb_completeness':
          comparison = (a.kb_completeness ?? 0) - (b.kb_completeness ?? 0);
          break;
        case 'company_type':
          comparison = a.company_type.localeCompare(b.company_type);
          break;
        case 'updated_at': {
          const dateA = a.last_kb_refresh ? new Date(a.last_kb_refresh).getTime() : 0;
          const dateB = b.last_kb_refresh ? new Date(b.last_kb_refresh).getTime() : 0;
          comparison = dateA - dateB;
          break;
        }
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return result;
  }, [companies, searchQuery, sortBy, sortOrder, filterType, filterStatus]);

  return {
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterType,
    setFilterType,
    filterStatus,
    setFilterStatus,
    filteredCompanies,
    resultCount: filteredCompanies.length,
    totalCount: companies.length,
    hasActiveFilters,
    clearFilters,
  };
}
